import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** Classes pour `<Input />` à l'intérieur du cadre bordé (sans double bordure). Hauteur 40px, coins resserrés côté shell. */
export const authFormFieldControlClassName = cn(
  'h-10 min-h-[40px] w-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-gray-900 shadow-none outline-none ring-0',
  'focus-visible:ring-0 placeholder:text-gray-500',
  'read-only:bg-gray-50 read-only:cursor-not-allowed read-only:text-gray-700',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const authButtonBase = cn(
  'inline-flex h-10 min-h-[40px] items-center justify-center gap-2 rounded-md text-sm font-medium',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

/** Bouton principal auth pleine largeur (40px, `rounded-md`). */
export const authPrimarySubmitClassName = cn(
  authButtonBase,
  'mt-2 w-full bg-[#20766F] text-white hover:bg-[#20766F]/90'
);

/** Bouton secondaire auth pleine largeur (contour gris). */
export const authSecondaryOutlineClassName = cn(
  authButtonBase,
  'mt-2 w-full border border-gray-400 bg-white text-[#20766F] hover:bg-gray-50 hover:text-[#20766F]/90'
);

/** Bouton principal auth en ligne (`flex-1`). */
export const authPrimaryRowClassName = cn(
  authButtonBase,
  'min-w-0 flex-1 bg-[#20766F] text-white hover:bg-[#20766F]/90'
);

/**
 * Champ auth : groupe sémantique, label shadcn, astérisque + texte sr-only « obligatoire »,
 * conteneur avec bordure et anneau focus (équivalent structure formulaire type design system).
 */
export function AuthFormField({
  id,
  label,
  required = false,
  invalid = false,
  children,
  hint,
  description,
  className,
}) {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      className={cn('flex flex-col gap-1', className)}
      data-slot="form-field"
      data-invalid={invalid ? 'true' : 'false'}
    >
      <Label
        htmlFor={id}
        id={`${id}__label`}
        className="text-sm font-medium leading-snug text-gray-700 gap-1"
      >
        <span>{label}</span>
        {required ? (
          <span className="text-red-600 inline-flex items-center gap-0">
            <span aria-hidden="true">*</span>
            <span className="sr-only">{t('auth.form.field_required')}</span>
          </span>
        ) : null}
      </Label>

      <div
        className={cn(
          'flex min-h-[40px] rounded-md border border-solid border-gray-400 bg-white overflow-hidden',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-[#20766F]/30 focus-within:ring-offset-2 focus-within:border-[#20766F]',
          invalid && 'border-red-500 focus-within:ring-red-500/25'
        )}
      >
        {children}
      </div>

      {description ? <div className="text-sm text-gray-700">{description}</div> : null}
      {hint}
    </div>
  );
}

AuthFormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  required: PropTypes.bool,
  invalid: PropTypes.bool,
  children: PropTypes.node.isRequired,
  hint: PropTypes.node,
  description: PropTypes.node,
  className: PropTypes.string,
};
